<template>
  <Modal
    content-class="delete-resource-modal"
    v-bind="modalProps"
    v-model="displayDeleteResourceModal"
    @confirm="confirmDelete"
    @cancel="closeDeleteModal">
    <va-disclaimer
      v-if="disclaimerText"
      :icon-alignment="'baseline'"
      class="mt-6"
      :text="disclaimerText"
      :type="'warning'"></va-disclaimer>
  </Modal>
</template>

<script>
  import Modal from "@/components/custom/Modal";
  import VaDisclaimer from "@/components/custom/VaDisclaimer";
  import { formErrorMessage } from "@/utility/formErrorMessage";

  export default {
    name: "DeleteResourceModal",
    components: {
      Modal,
      VaDisclaimer,
    },
    props: {
      resourceName: {
        type: String,
        default: "",
      },
      deleteManyDisclaimer: {
        type: String,
        default: "",
      },
      deleteOneDisclaimer: {
        type: String,
        default: "",
      },
    },
    data() {
      return {
        displayDeleteResourceModal: false,
        deleteResourceContextData: {},
        itemsToDelete: [],
        disclaimerText: "",
      };
    },
    computed: {
      // True when deleting multiple list items
      deletingMany() {
        return this.itemsToDelete.length > 1;
      },
      // Pages affected if list item(-s) deleted
      affectedPages() {
        return this.itemsToDelete.reduce((sum, current) => {
          return sum + (current.pagesCount ? current.pagesCount : 0);
        }, 0);
      },
      modalProps() {
        let title = this.$t("generic.delete_resource", {
          resource: this.getResourceTitle(this.itemsToDelete.length).toLowerCase(),
        });

        if (["authors"].includes(this.resourceName)) {
          if (this.deletingMany) {
            title = this.$t("generic.delete_many_disclaimer_used_in", {
              resource: this.getResourceTitle().toLowerCase(),
              count: this.itemsToDelete.length,
              usedInPagesCount: this.affectedPages,
            });
          } else {
            title = this.$tc("overrides.confirm.delete_resource_and_name", this.affectedPages, {
              resource: this.getResourceTitle().toLowerCase(),
              name: this.itemsToDelete[0]?.name,
            });
          }
        }

        return {
          title,
          subtitle: this.$t("generic.delete_message"),
          "cta-color": "error",
          "cta-text": this.$t("generic.buttons.delete"),
          "dialog-icon": "error",
        };
      },
    },
    beforeMount() {
      // Display / hide modal and set deletable items
      this.$watch(`$store.state.${this.resourceName}.displayDeleteModal`, displayModal => {
        this.displayDeleteResourceModal = displayModal;

        const { itemsToDelete, deleteResourceContextData } = this.$store.state[this.resourceName];

        this.itemsToDelete = itemsToDelete;
        this.deleteResourceContextData = deleteResourceContextData;

        if (!displayModal) return;

        // Display disclaimer only when pages are affected and only when deleting headers/footers/authors
        if (this.affectedPages > 0 && ["footers", "headers", "calendars"].includes(this.resourceName)) {
          this.disclaimerText = this.$tc(
            `${
              this.deletingMany
                ? `${this.deleteManyDisclaimer ? this.deleteManyDisclaimer : "generic.delete_many_disclaimer_default"}`
                : `${this.deleteOneDisclaimer ? this.deleteOneDisclaimer : "generic.delete_one_disclaimer_default"}`
            }`,
            this.affectedPages,
            {
              resource: this.getResourceTitle(itemsToDelete.length).toLowerCase(),
              resourceSingular: this.getResourceTitle().toLowerCase(),
              pages: this.affectedPages,
            },
          );
        } else {
          this.disclaimerText = "";
        }
      });
    },
    methods: {
      closeDeleteModal() {
        this.$store.dispatch(`${this.resourceName}/displayDeleteModal`, {
          displayDeleteModal: false,
        });
      },
      async confirmDelete() {
        if (this.deletingMany) {
          try {
            await this.$store.dispatch(`${this.resourceName}/deleteMany`, {
              ids: this.itemsToDelete.map(({ id }) => id),
            });
            this.deleteResourceContextData.postDeleteCallback();
          } catch (e) {
            this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
          } finally {
            this.closeDeleteModal();
          }
        } else {
          try {
            await this.$admin.dataProvider.customHttpCall("DELETE", `${this.resourceName}/${this.itemsToDelete[0].id}`);
            this.$admin.toast.success(
              this.$t("generic.resource_deleted_successfully", {
                resource: this.getResourceTitle(this.itemsToDelete.length),
              }),
            );
            await this.$store.dispatch("api/refresh", this.resourceName);
          } catch (e) {
            this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
          } finally {
            this.closeDeleteModal();
          }
        }
      },
      getResourceTitle(count = 1) {
        return this.$tc(`resources.${this.resourceName}.name`, count);
      },
    },
  };
</script>

<style lang="scss">
  .delete-resource-modal {
    .va-disclaimer {
      .va-disclaimer__icon {
        margin-top: 3px;
      }
    }
  }
</style>
